import { useState } from "react"
import dayjs from "dayjs"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type {
  ContactChannel,
  ContactCounterpart,
  ContactDetail,
  ContactDirection,
  ContactOutcome,
  ContactPhoneLine,
  ContactResult,
  DunningCaseItem,
  DunningContactLogEntry,
} from "../types"

const contactLogsBase = (id: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${id}/contact_logs`

export type ContactLogInput = {
  channel: ContactChannel
  direction: ContactDirection
  /** datetime-local value ("YYYY-MM-DDTHH:mm"); sent to the API as ISO8601. */
  contactedAtLocal: string
  /** 旧「結果」。編集時の互換のために残す（新規は contactResult を使う） */
  outcome: ContactOutcome | ""
  phoneLine: ContactPhoneLine | ""
  counterpart: ContactCounterpart | ""
  contactResult: ContactResult | ""
  contactDetail: ContactDetail | ""
  note: string
  countsAsContact: boolean
}

export type ContactLogHistoryPage = {
  data: DunningContactLogEntry[]
  meta: {
    page: number
    per: number
    prevPage: number | null
    nextPage: number | null
    totalCount: number
    totalPages: number
  }
}

/** API に送る本文。画面の冪等キーも、この内容が同じかどうかで使い回しを決める */
export const toContactLogPayload = (input: ContactLogInput) => {
  const note = input.note.trim()
  return {
    channel: input.channel,
    direction: input.direction,
    contactedAt: dayjs(input.contactedAtLocal).toISOString(),
    countsAsContact: input.countsAsContact,
    // Omit empty optionals so we never persist empty-string values.
    ...(input.outcome ? { outcome: input.outcome } : {}),
    ...(input.phoneLine ? { phoneLine: input.phoneLine } : {}),
    ...(input.counterpart ? { counterpart: input.counterpart } : {}),
    ...(input.contactResult ? { contactResult: input.contactResult } : {}),
    ...(input.contactDetail ? { contactDetail: input.contactDetail } : {}),
    ...(note ? { note } : {}),
  }
}

/**
 * Operator contact-log actions on a dunning case (record / edit / soft-delete).
 * Each call returns the refreshed case item so callers can update local state
 * (facts.contact + the new audit-log entry) without a separate fetch.
 */
export const useDunningContactLogs = (id: number) => {
  const { getWithToken, postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    setIsSubmitting(true)
    setError(null)
    try {
      return await fn()
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "操作に失敗しました。"
      setError(message)
      return undefined
    } finally {
      setIsSubmitting(false)
    }
  }

  /** operationKey は再送・二重送信で記録が増えないようにする冪等キー（同じ入力の再送は同じキーで送る） */
  const logContact = (input: ContactLogInput, operationKey: string) =>
    run(async () => {
      const res = await postWithToken(contactLogsBase(id), {
        ...toContactLogPayload(input),
        operationKey,
      })
      return res.data as DunningCaseItem
    })

  const updateContactLog = (contactLogId: number, input: ContactLogInput) =>
    run(async () => {
      const res = await patchWithToken(
        `${contactLogsBase(id)}/${contactLogId}`,
        toContactLogPayload(input)
      )
      return res.data as DunningCaseItem
    })

  const deleteContactLog = (contactLogId: number) =>
    run(async () => {
      const res = await deleteWithToken(`${contactLogsBase(id)}/${contactLogId}`)
      return res.data as DunningCaseItem
    })

  const loadContactLogs = (page: number) =>
    run(async () => {
      const res = await getWithToken(contactLogsBase(id), { params: { page, per: 50 } })
      return res.data as ContactLogHistoryPage
    })

  return {
    logContact,
    updateContactLog,
    deleteContactLog,
    loadContactLogs,
    isSubmitting,
    error,
  }
}
